export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: string
  ) {
    super(`${status}: ${body}`);
    this.name = 'HttpError';
  }
}

export class FetchService {
  private _baseUrl: string;

  constructor(baseUrl: string) {
    this._baseUrl = baseUrl;
  }

  private async getResponsePayload<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
      // Response is likely JSON
      return response.json() as T;
    } else if (contentType && contentType.includes('text/plain')) {
      // Response is likely plain text
      return response.text() as T;
    } else if (
      contentType &&
      contentType.includes('application/octet-stream')
    ) {
      // Response is likely binary data (e.g., a file download)
      return response.blob() as T;
    } else {
      // Handle other or unknown content types
      return response.text() as T; // Default to text if unsure
    }
  }

  async fetch<T>(relativeUrl: string, requestInit: RequestInit): Promise<T> {
    const url = `${this._baseUrl}/${relativeUrl}`;
    try {
      const response = await fetch(url, {
        ...requestInit,
        headers: {
          'Content-Type': 'application/json',
          ...requestInit.headers,
        },
      });
      if (!response.ok) {
        const errorData = await this.getResponsePayload<unknown>(response);
        console.error(`API error ${url} (${response.status}):`, errorData);
        // Include status code in error message for proper error handling
        const errorMessage =
          typeof errorData === 'object'
            ? JSON.stringify(errorData)
            : String(errorData);
        throw new HttpError(response.status, errorMessage);
      }
      return this.getResponsePayload<T>(response);
    } catch (err) {
      console.error('API fetch error:', {
        url: url,
        error: (err as Error).message,
        METHOD: requestInit.method,
      });
      throw err;
    }
  }
}
